import { useEffect, useState } from "react";
import { useTranslations } from "@shared/context/useTranslations";
import { Container } from "../../Atoms/Container/Container.component";
import { FrontendPage } from "../FrontendPage/FrontendPage.component";
import { useLocation, useParams } from "react-router";
import { CraneSearchBlock } from "@shared/components/Organisms/CraneSearchBlock/CraneSearchBlock.component";
import { CraneSerialSearch } from "@shared/components/Templates/CraneSerialSearch/CraneSerialSearch.component";
import { useCrane } from "@shared/context/CraneContext";
import { Icon } from "@shared/components/Molecules/Icon/Icon.component";
import { IconNames } from "@shared/enums/icons.enums";
import { CraneSearchResults } from "@shared/components/Organisms/CraneSearchResults/CraneSearchResults.component";
import { useSearchParams } from "react-router-dom";
import ProductItem from "../Productitem/ProductItem.component";
import { ProductPageProps } from "@shared/types/ProductTypes.interface";
import { PageLoader } from "@shared/components/Atoms/PageLoader/PageLoader.component";
import { cn } from "@shared/helpers/classNames.helper";
import S from "./CranePage.module.scss";

interface CranePageProps {
    hideSidebar?: boolean;
}

enum CranePageType {
    Default = "Default",
    CategoryPage = "CategoryPage",
    ProductPage = "ProductPage",
}

export const CranePage = ({ hideSidebar }: CranePageProps) => {
    const params = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {
        isCraneMode,
        craneInfo,
        serialNumber,
        categoryId,
        setCategoryId,
        productId,
        setProductId,
        searchQuery,
        setSearchQuery,
        fetchCranePageByUrl,
        pageContent,
        isLoading,
    } = useCrane();
    const { getTranslation } = useTranslations();
    const [pageType, setPageType] = useState<CranePageType>(CranePageType.Default);

    useEffect(() => {
        if (isCraneMode) {
            const searchParam = searchParams.get("query") || "";
            if (searchParam !== searchQuery) {
                setSearchQuery(searchParam);
            }
        }
    }, [isCraneMode, searchParams, searchQuery, setSearchQuery]);

    useEffect(() => {
        if (isCraneMode) {
            const data = location.state as { category?: number; product?: number } | undefined;

            if (data?.category && data.category !== categoryId) {
                setCategoryId(data.category);
                setProductId(0);
                setSearchQuery("");
                setPageType(CranePageType.CategoryPage);
            } else if (data?.product && data.product !== productId) {
                setProductId(data.product);
                setCategoryId(0);
                setSearchQuery("");

                // Start loading page content for product
                fetchCranePageByUrl(craneInfo?.serialNumber || "", location.pathname);
                setPageType(CranePageType.ProductPage);
            }
        }
    }, [
        categoryId,
        craneInfo?.serialNumber,
        fetchCranePageByUrl,
        isCraneMode,
        location.pathname,
        location.state,
        productId,
        setCategoryId,
        setProductId,
        setSearchQuery,
    ]);

    useEffect(() => {
        // Handle full page reload with serial number in url
        if (
            !isCraneMode &&
            params.serialnumber &&
            craneInfo?.serialNumber !== params.serialnumber
        ) {
            fetchCranePageByUrl(params.serialnumber as string, window.location.pathname);
        }
    }, [craneInfo, craneInfo?.serialNumber, fetchCranePageByUrl, isCraneMode, params]);

    useEffect(() => {
        if ((!categoryId || categoryId === 0) && (!productId || productId === 0)) {
            setPageType(CranePageType.Default);
        }
        if (categoryId && categoryId > 0) {
            setPageType(CranePageType.CategoryPage);
        }
        if (productId && productId > 0) {
            setPageType(CranePageType.ProductPage);
        }
    }, [categoryId, productId]);

    return (
        <>
            <PageLoader isPageLoading={isLoading} />
            {isCraneMode ? (
                <FrontendPage
                    {...{
                        name: "Crane Page",
                        hideSidebar: hideSidebar || false,
                    }}
                >
                    {pageType === CranePageType.ProductPage ? (
                        <>
                            {productId === pageContent?.productId ? (
                                <ProductItem {...(pageContent as ProductPageProps)} />
                            ) : null}
                        </>
                    ) : pageType === CranePageType.CategoryPage ? (
                        <>
                            {/* Display products for crane category */}
                            <CraneSearchResults />
                        </>
                    ) : (
                        <>
                            <CraneSearchBlock />

                            {/* Display products for crane search query */}
                            <CraneSearchResults />
                        </>
                    )}
                </FrontendPage>
            ) : (
                <Container>
                    <CraneSerialSearch />
                    <div
                        className={cn(S.emptyState, { [S.notFound]: serialNumber && !isCraneMode })}
                    >
                        <Icon iconName={IconNames.Search} className={S.emptySearchIcon} />
                        <h2 className={S.emptyTitle}>{getTranslation("cranePage.emptyTitle")}</h2>
                        <p className={S.emptyDescription}>
                            {getTranslation("cranePage.emptyDescription")}
                        </p>
                    </div>
                </Container>
            )}
            ;
        </>
    );
};
